<template>
  <div>
    <GroupConfig
      v-if="currentCapabilityCode === '0'"
      ref="groupConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <PopupPushConfig
      v-else-if="currentCapabilityCode === '1'"
      ref="popupPushConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <PayLinkConfig
      v-else-if="currentCapabilityCode === '2'"
      ref="payLinkConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <AdStrategyConfig
      v-else-if="currentCapabilityCode === '3'"
      ref="adStrategyConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <SimpleGroupFieldConfig
      v-else-if="currentCapabilityCode === '4'"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
      :flat="true"
      label="广告任务上限"
      unit="次"
      fieldKey="maxAdWatchCount"
    />

    <PaymentPanelConfig
      v-else-if="currentCapabilityCode === '5'"
      ref="paymentPanelConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :controlDisabled="isReadonlyControlGroup"
      :disabled="disabled"
    />

    <SimpleGroupFieldConfig
      v-else-if="currentCapabilityCode === '6'"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :controlDisabled="isReadonlyControlGroup"
      controlEmptyText="跟随剧集设置"
      :disabled="disabled"
      label="剧集收费"
      unit="金币/集"
      :flat="true"
      fieldKey="chargeCoins"
    />

    <UnlockLayerConfig
      v-else-if="currentCapabilityCode === '7'"
      ref="unlockLayerConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <ActivityPushConfig
      v-else-if="currentCapabilityCode === '8'"
      ref="activityPushConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <ThirdPayConfig
      v-else-if="currentCapabilityCode === '9'"
      ref="thirdPayConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />

    <RechargeTemplateConfig
      v-else-if="currentCapabilityCode === '10'"
      ref="rechargeTemplateConfig"
      :formData="formData"
      :experimentGroupCount="experimentGroupCount"
      :disabled="disabled"
    />
  </div>
</template>

<script>
import { getDefaultGroup } from '@/api/abtest'
import { getCrowdList } from '@/api/crowd'
import {
  createGroupParamByCapability,
  isReadonlyControlGroupCapability,
  normalizeGroupParamByCapability,
  toCapabilityCode
} from '../helpers'
import ActivityPushConfig from './ActivityPushConfig.vue'
import AdStrategyConfig from './AdStrategyConfig.vue'
import GroupConfig from './GroupConfig.vue'
import PayLinkConfig from './PayLinkConfig.vue'
import PaymentPanelConfig from './PaymentPanelConfig.vue'
import PopupPushConfig from './PopupPushConfig.vue'
import SimpleGroupFieldConfig from './SimpleGroupFieldConfig.vue'
import ThirdPayConfig from './ThirdPayConfig.vue'
import UnlockLayerConfig from './UnlockLayerConfig.vue'
import RechargeTemplateConfig from './RechargeTemplateConfig.vue'

export default {
  name: 'ExperimentCapabilityConfig',
  components: {
    ActivityPushConfig,
    AdStrategyConfig,
    GroupConfig,
    PayLinkConfig,
    PaymentPanelConfig,
    PopupPushConfig,
    SimpleGroupFieldConfig,
    ThirdPayConfig,
    UnlockLayerConfig,
    RechargeTemplateConfig
  },
  props: {
    formData: {
      type: Object,
      default: () => ({
        groups: []
      })
    },
    experimentGroupCount: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentCapabilityCode() {
      return toCapabilityCode(this.formData.experimentCapability)
    },
    isReadonlyControlGroup() {
      return isReadonlyControlGroupCapability(this.currentCapabilityCode)
    }
  },
  methods: {
    getCrowdItemCountryCodes(item) {
      if (!item || typeof item !== 'object') return []

      const countryList =
        item.countryCodes ||
        item.countrycodes ||
        item.countryCodeList ||
        item.countryCodelist ||
        []

      return Array.isArray(countryList) ? countryList : []
    },
    async getCrowdCountries() {
      const currentCountries = Array.isArray(this.formData.crowdCountryCodes)
        ? Array.from(new Set(this.formData.crowdCountryCodes.filter(Boolean)))
        : []
      if (currentCountries.length) return currentCountries

      const crowdIds = Array.isArray(this.formData.crowdIds)
        ? this.formData.crowdIds
        : []
      if (!crowdIds.length) return []

      const selectedIdSet = new Set(crowdIds.map((item) => String(item)))
      const formCrowdOptions = Array.isArray(this.formData.crowdOptions)
        ? this.formData.crowdOptions
        : []
      const optionCountries = Array.from(
        new Set(
          formCrowdOptions
            .filter((item) => selectedIdSet.has(String(item.id)))
            .reduce((acc, item) => {
              acc.push(...this.getCrowdItemCountryCodes(item))
              return acc
            }, [])
            .filter(Boolean)
        )
      )
      if (optionCountries.length) {
        this.$set(this.formData, 'crowdCountryCodes', optionCountries)
        return optionCountries
      }

      const { data } = await getCrowdList({
        current: -1,
        size: -1
      })
      const countries = Array.from(
        new Set(
          (data.records || [])
            .filter((item) => selectedIdSet.has(String(item.id)))
            .reduce((acc, item) => {
              acc.push(...this.getCrowdItemCountryCodes(item))
              return acc
            }, [])
            .filter(Boolean)
        )
      )

      this.$set(this.formData, 'crowdCountryCodes', countries)
      return countries
    },
    getCurrentTargetRef() {
      const refKeyMap = {
        0: 'groupConfig',
        1: 'popupPushConfig',
        2: 'payLinkConfig',
        3: 'adStrategyConfig',
        5: 'paymentPanelConfig',
        7: 'unlockLayerConfig',
        8: 'activityPushConfig',
        9: 'thirdPayConfig',
        10: 'rechargeTemplateConfig'
      }

      return this.$refs[refKeyMap[this.currentCapabilityCode]]
    },
    createGroupParam() {
      return createGroupParamByCapability(this.currentCapabilityCode)
    },
    normalizeGroupParam(param) {
      return normalizeGroupParamByCapability(this.currentCapabilityCode, param)
    },
    syncGroupParams() {
      this.resetControlGroupParam(false)
      this.formData.groups = this.formData.groups.map((group, index) => ({
        ...group,
        experimentGroupName:
          group.experimentGroupName || '实验组' + (index + 1),
        experimentGroupParam: this.normalizeGroupParam(
          group.experimentGroupParam
        )
      }))
    },
    resetControlGroupParam(forceReset = true) {
      if (forceReset || this.formData.controlGroupParam === undefined) {
        this.$set(this.formData, 'controlGroupParam', this.createGroupParam())
        return
      }

      this.$set(
        this.formData,
        'controlGroupParam',
        this.normalizeGroupParam(this.formData.controlGroupParam)
      )
    },
    async applyDefaultGroup(defaultParam) {
      const normalized = this.normalizeGroupParam(defaultParam)
      this.$set(this.formData, 'controlGroupParam', normalized)

      const target = this.getCurrentTargetRef()
      if (target && target.applyDefaultGroup) {
        await target.applyDefaultGroup(normalized)
      }
    },
    async handleGetDefaultGroup() {
      if (this.currentCapabilityCode === '0') {
        const target = this.getCurrentTargetRef()
        if (target && target.handleGetDefaultGroup) {
          await target.handleGetDefaultGroup()
        }
        return
      }

      if (['4', '5', '6'].includes(this.currentCapabilityCode)) {
        const params = {
          playIds:
            Array.isArray(this.formData.playIds) && this.formData.playIds.length
              ? this.formData.playIds.join(',')
              : undefined
        }

        if (this.currentCapabilityCode === '5') {
          const countries = await this.getCrowdCountries()
          if (countries.length) {
            params.countries = countries.join(',')
          }
        }

        const { data } = await getDefaultGroup(params)
        await this.applyDefaultGroup(data)
      }
    },
    validate() {
      const target = this.getCurrentTargetRef()
      if (!target || !target.validate) return true
      return target.validate()
    }
  }
}
</script>